/*
  Product page helpers for sections/capra-product.liquid.

  <capra-submit-price>
    The price shown inside the Add to cart button. When a variant is picked,
    product-info.js refetches the section and publishes variantChange with the
    new markup, and this copies the price out of it. When the optional
    quantity stepper is present it multiplies the variant's unit price
    (data-unit-price, in cents) by the quantity using the shop's money format
    through window.capraFormatMoney (capra-money.js, loaded by the layout).
    product-form.js only ever rewrites the first <span> in the button, so the
    price survives sold-out toggles; CSS hides it while the button is disabled.

  <capra-save-button>
    The heart beside Add to cart, and on each card of the Saved page
    (capra-saved.liquid). Toggles the product handle in a localStorage list so
    the state survives reloads, and publishes capra_product_saved or
    capra_product_unsaved for a custom pixel to count. There is no
    account-backed wishlist yet.
*/
if (!customElements.get('capra-submit-price')) {
  customElements.define(
    'capra-submit-price',
    class CapraSubmitPrice extends HTMLElement {
      connectedCallback() {
        this.unitText = this.textContent.trim();
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, html } }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || productInfo.sectionId !== sectionId) return;
          const next = html.querySelector('capra-submit-price');
          if (!next) return;
          this.unitText = next.textContent.trim();
          this.dataset.unitPrice = next.dataset.unitPrice || '';
          this.render();
        });

        // The optional quantity stepper lives in the same form; its change
        // events bubble up to the form element.
        this.form = this.closest('form');
        this.onQuantityChange = (event) => {
          if (event.target.name === 'quantity') this.render();
        };
        this.form?.addEventListener('change', this.onQuantityChange);
        this.render();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.form?.removeEventListener('change', this.onQuantityChange);
      }

      get quantity() {
        const input = this.form?.querySelector('input[name="quantity"]');
        const value = parseInt(input?.value, 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      render() {
        const unit = parseInt(this.dataset.unitPrice, 10);
        const quantity = this.quantity;
        if (quantity === 1 || !Number.isFinite(unit) || !this.dataset.moneyFormat) {
          this.textContent = this.unitText;
          return;
        }
        this.textContent = CapraSubmitPrice.formatMoney(unit * quantity, this.dataset.moneyFormat);
      }

      static formatMoney(cents, format) {
        return window.capraFormatMoney(cents, format);
      }
    }
  );
}

if (!customElements.get('capra-save-button')) {
  customElements.define(
    'capra-save-button',
    class CapraSaveButton extends HTMLElement {
      static storageKey = 'capra-saved-products';

      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.button.addEventListener('click', () => this.toggle());
        this.render();
      }

      get saved() {
        try {
          const list = JSON.parse(window.localStorage.getItem(CapraSaveButton.storageKey));
          return Array.isArray(list) ? list : [];
        } catch {
          return [];
        }
      }

      set saved(list) {
        try {
          window.localStorage.setItem(CapraSaveButton.storageKey, JSON.stringify(list));
        } catch {
          // Private mode or storage disabled: the state just will not persist.
        }
      }

      get isSaved() {
        return this.saved.includes(this.dataset.handle);
      }

      toggle() {
        const list = this.saved;
        const index = list.indexOf(this.dataset.handle);
        if (index === -1) {
          list.push(this.dataset.handle);
        } else {
          list.splice(index, 1);
        }
        this.saved = list;
        this.render();
        window.Shopify?.analytics?.publish?.(index === -1 ? 'capra_product_saved' : 'capra_product_unsaved', {
          handle: this.dataset.handle,
          title: this.dataset.title,
          page: this.dataset.page,
        });
      }

      render() {
        const saved = this.isSaved;
        this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        this.button.setAttribute('aria-label', saved ? this.dataset.labelSaved : this.dataset.labelSave);
      }
    }
  );
}
